import { Variant, Product } from "../../types/types";
import { Smush } from "../../types/helpers";
import {
  generateBreadcrumbs,
  generateDeliveryOptions,
  generatePageName,
  generatePromotions,
  generatePromotionClassifications,
} from "./helpers";
import { ensureIsActive } from "../product/ensureIsActive";
import { getDefaultCategory } from "../product/getDefaultCategory";

type JLDataType = {
  recsModel: Record<string, never>;
  page: {
    locale: {
      country: "uk";
      currency: "gbp";
      language: "en-gb";
    };
    pageInfo: {
      breadCrumb: {
        clickable: number;
        value: string;
      }[];
      pageCanonicalURL: string;
      pageName: string;
      pageType: string;
      pageConfigName: string;
      site: "johnlewis.com";
    };
    productPages: {
      brand: string;
      bundles: [];
      colour: string;
      deliveryOptions: string;
      displayedSku: string;
      emwbisForm: boolean | "";
      numberOfAvailableProductSkus: number;
      numberOfProductSkus: number;
      outOfStockProductSkus: number;
      price: string;
      productId: string;
      productName: string;
      category: string;
      productLifecycle: "retired product" | "";
      productViewType: "standard product" | "main product";
      promotionClassifications: {
        group: string;
        type: string;
      }[];
      promotions: string;
      is2ManDelivery: boolean;
      isM2M: boolean;
      isSoldByLength: boolean;
      video: [];
      content: Record<string, never>;
      recommendations: Record<string, never>;
      reviews: {
        averageRating: number;
        totalNumber: number;
      };
      services: [];
      size: string;
      yourviewed: Record<string, never>;
      showShopTheLook: string;
    };
  };
};

export const generatePageLoadAnalyticsDataLayer = (
  product: Smush<Product>,
  selectedVariant: Variant,
  pageConfigName?: string,
): JLDataType => {
  const variants: Variant[] = product.variants;
  const availableProductSkuCount = variants.filter(
    (variant) => variant.availability?.availableToOrder === true,
  ).length;

  return {
    recsModel: {},
    page: {
      locale: {
        country: "uk",
        currency: "gbp",
        language: "en-gb",
      },
      pageInfo: {
        breadCrumb: generateBreadcrumbs(product),
        pageCanonicalURL: `${window.location.protocol}//${window.location.host}${product.canonicalURL}`,
        pageName: generatePageName(product),
        pageType: `pdp scaffold product`,
        pageConfigName: `${pageConfigName || ""}`,
        site: "johnlewis.com",
      },
      productPages: {
        brand: product.brand?.name || "",
        bundles: [],
        colour: selectedVariant.differentiators?.colour || "",
        deliveryOptions: generateDeliveryOptions(product),
        displayedSku: selectedVariant.aliases.skuId,
        emwbisForm: "",
        numberOfAvailableProductSkus: availableProductSkuCount,
        numberOfProductSkus: variants.length,
        outOfStockProductSkus: variants.length - availableProductSkuCount,
        price: selectedVariant.price?.value || "",
        productId: product.aliases.pimProductId,
        productName: product.title,
        category: getDefaultCategory(product) || "",
        productLifecycle: ensureIsActive(product) ? "" : "retired product",
        productViewType: "main product",
        promotionClassifications: generatePromotionClassifications(
          product,
          selectedVariant,
        ),
        promotions: generatePromotions(product),
        is2ManDelivery: false,
        isM2M: false,
        isSoldByLength: false,
        video: [],
        content: {},
        recommendations: {},
        reviews: product.reviewSummary
          ? {
              averageRating: product.reviewSummary.averageRating,
              totalNumber: product.reviewSummary.numberOfReviews,
            }
          : {
              averageRating: 0,
              totalNumber: 0,
            },
        services: [],
        size: selectedVariant.differentiators?.size || "",
        yourviewed: {},
        showShopTheLook: "",
      },
    },
  };
};
